import {
  PERMISSION_KEYS,
  PERMISSIONS,
  ROLE_LABELS,
  ROLE_PERMISSIONS,
  ROLES,
  type DepartmentDto,
  type Page,
  type Permission,
  type Role,
  type UserDto,
} from '@edms/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Icon } from '../lib/icons';
import { UserSelect } from '../lib/tasks-ui';
import { Empty, Pager, Person, relative, useDebounced, useToast } from '../lib/ui';

const LIMIT = 50;

function useDepartments() {
  return useQuery({ queryKey: ['departments'], queryFn: () => api.get<DepartmentDto[]>('/departments'), staleTime: 60_000 });
}

export function UsersPage() {
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [active, setActive] = useState('true');
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState<UserDto | 'new' | null>(null);
  const q = useDebounced(search);
  const departments = useDepartments();

  useEffect(() => setOffset(0), [q, role, departmentId, active]);

  const users = useQuery({
    queryKey: ['users', { q, role, departmentId, active, offset }],
    queryFn: () => api.get<Page<UserDto>>('/users', { search: q, role, departmentId, active, limit: LIMIT, offset }),
    placeholderData: (prev) => prev,
  });

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Сотрудники</h1>
          <p className="muted">{users.data ? `${users.data.total} человек` : 'Загрузка…'}</p>
        </div>
        <button className="btn btn-primary" onClick={() => setEditing('new')}>
          <Icon name="plus" size={18} /> Добавить
        </button>
      </div>

      <div className="card">
        <div className="toolbar">
          <input className="input" type="search" placeholder="Поиск по ФИО, email, должности" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Поиск" />
          <select className="select" value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} aria-label="Отдел">
            <option value="">Все отделы</option>
            {departments.data?.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          <select className="select" value={role} onChange={(e) => setRole(e.target.value)} aria-label="Роль">
            <option value="">Все роли</option>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </select>
          <select className="select" value={active} onChange={(e) => setActive(e.target.value)} aria-label="Статус">
            <option value="true">Активные</option>
            <option value="false">Отключённые</option>
            <option value="all">Все</option>
          </select>
        </div>

        {users.data?.items.length === 0 ? (
          <Empty emoji="🔍" title="Никого не нашли">Попробуйте изменить фильтры</Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Сотрудник</th>
                  <th className="hide-mobile">Отдел</th>
                  <th>Роль</th>
                  <th className="hide-mobile">Был в системе</th>
                </tr>
              </thead>
              <tbody>
                {users.data?.items.map((u) => (
                  <tr key={u.id} className="clickable" onClick={() => setEditing(u)} tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setEditing(u)}>
                    <td>
                      <Person name={u.fullName} sub={[u.position, u.email].filter(Boolean).join(' · ')} />
                    </td>
                    <td className="hide-mobile">{u.departmentName ?? <span className="faint">—</span>}</td>
                    <td>
                      <span className={`badge${u.role === 'admin' ? ' badge-brand' : ''}`}>{ROLE_LABELS[u.role]}</span>
                      {!u.isActive && (
                        <span className="badge badge-danger" style={{ marginLeft: 6 }}>
                          отключён
                        </span>
                      )}
                      {u.permissions.length > 0 && (
                        <span className="badge badge-warn" style={{ marginLeft: 6 }} title={u.permissions.map((p) => PERMISSIONS[p]).join('\n')}>
                          +{u.permissions.length}
                        </span>
                      )}
                    </td>
                    <td className="hide-mobile faint">{relative(u.lastSeenAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {users.data && <Pager total={users.data.total} limit={LIMIT} offset={offset} onChange={setOffset} />}
      </div>

      {editing && <UserDrawer user={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

interface FormState {
  email: string;
  fullName: string;
  role: Role;
  departmentId: string;
  position: string;
  managerId: string;
  isActive: boolean;
  permissions: Permission[];
}

function toForm(u: UserDto | null): FormState {
  return {
    email: u?.email ?? '',
    fullName: u?.fullName ?? '',
    role: u?.role ?? 'employee',
    departmentId: u?.departmentId ?? '',
    position: u?.position ?? '',
    managerId: u?.managerId ?? '',
    isActive: u?.isActive ?? true,
    permissions: u?.permissions ?? [],
  };
}

function UserDrawer({ user, onClose }: { user: UserDto | null; onClose: () => void }) {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const departments = useDepartments();
  const [form, setForm] = useState<FormState>(() => toForm(user));
  const isAdmin = me!.role === 'admin';
  const isSelf = user?.id === me!.id;
  const managers = useQuery({
    queryKey: ['users', 'all-active'],
    queryFn: () => api.get<Page<UserDto>>('/users', { limit: 200 }),
    staleTime: 60_000,
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  const save = useMutation({
    mutationFn: () => {
      const body = {
        email: form.email,
        fullName: form.fullName,
        departmentId: form.departmentId || null,
        position: form.position,
        managerId: form.managerId || null,
        ...(isAdmin && !isSelf ? { role: form.role, permissions: form.permissions } : {}),
        ...(user && !isSelf ? { isActive: form.isActive } : {}),
      };
      return user ? api.patch<UserDto>(`/users/${user.id}`, body) : api.post<UserDto>('/users', body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      void qc.invalidateQueries({ queryKey: ['departments'] });
      toast(user ? 'Изменения сохранены' : 'Сотрудник добавлен, приглашение отправлено на почту');
      onClose();
    },
  });

  const reset = useMutation({
    mutationFn: () => api.post(`/users/${user!.id}/send-password-reset`),
    onSuccess: () => toast('Ссылка для смены пароля отправлена'),
    onError: (e) => toast(e.message, { error: true }),
  });

  const err = save.error instanceof ApiError ? save.error : null;
  const fieldError = (k: string) => err?.fields[k] && <span className="field-error">{err.fields[k]}</span>;
  const rolePerms = new Set(ROLE_PERMISSIONS[form.role]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <form className="drawer" role="dialog" aria-modal="true" aria-label={user ? 'Редактирование сотрудника' : 'Новый сотрудник'} onSubmit={submit}>
        <header>
          <h2>{user ? user.fullName : 'Новый сотрудник'}</h2>
          <button type="button" className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Закрыть">
            <Icon name="close" />
          </button>
        </header>
        <div className="body">
          {err && (
            <div className="alert">
              {err.message}
              {/* Ошибки полей без своего места в форме (права, роль, отдел) тоже показываем. */}
              {Object.entries(err.fields)
                .filter(([k]) => !['fullName', 'email', 'managerId'].includes(k))
                .map(([k, m]) => (
                  <div key={k} className="faint">
                    {k.startsWith('permissions') ? 'Права' : k}: {m}
                  </div>
                ))}
            </div>
          )}
          <label className="field">
            <span>ФИО</span>
            <input className="input" required value={form.fullName} onChange={(e) => set('fullName', e.target.value)} aria-invalid={Boolean(err?.fields.fullName)} />
            {fieldError('fullName')}
          </label>
          <label className="field">
            <span>Email</span>
            <input className="input" type="email" required value={form.email} onChange={(e) => set('email', e.target.value)} aria-invalid={Boolean(err?.fields.email)} />
            {fieldError('email')}
          </label>
          <div className="two-cols">
            <label className="field">
              <span>Отдел</span>
              <select className="select" value={form.departmentId} onChange={(e) => set('departmentId', e.target.value)}>
                <option value="">—</option>
                {departments.data?.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Должность</span>
              <input className="input" value={form.position} onChange={(e) => set('position', e.target.value)} />
            </label>
          </div>
          <label className="field">
            <span>Непосредственный руководитель</span>
            <select className="select" value={form.managerId} onChange={(e) => set('managerId', e.target.value)}>
              <option value="">—</option>
              {managers.data?.items
                .filter((m) => m.id !== user?.id)
                .map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.fullName}
                  </option>
                ))}
            </select>
            <span className="faint">Используется в маршрутах: «Руководитель инициатора»</span>
            {fieldError('managerId')}
          </label>


          {isAdmin && !isSelf && (
            <fieldset>
              <legend>Роль и права</legend>
              <select className="select" value={form.role} onChange={(e) => set('role', e.target.value as Role)} aria-label="Роль">
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </option>
                ))}
              </select>
              <span className="faint">Отмеченные серым права уже даёт роль. Остальные можно выдать лично.</span>
              {PERMISSION_KEYS.map((p) => {
                const fromRole = rolePerms.has(p);
                return (
                  <label key={p} className="check" style={fromRole ? { opacity: 0.55 } : undefined}>
                    <input
                      type="checkbox"
                      disabled={fromRole}
                      checked={fromRole || form.permissions.includes(p)}
                      onChange={(e) =>
                        set('permissions', e.target.checked ? [...form.permissions, p] : form.permissions.filter((x) => x !== p))
                      }
                    />
                    <span>{PERMISSIONS[p]}</span>
                  </label>
                );
              })}
            </fieldset>
          )}

          {user && !isSelf && (
            <fieldset>
              <legend>Доступ</legend>
              <label className="check">
                <input type="checkbox" checked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} />
                <span>
                  Учётная запись активна
                  <br />
                  <span className="faint">Отключённый сотрудник сразу теряет доступ; история его действий сохраняется.</span>
                </span>
              </label>
              <button type="button" className="btn" onClick={() => reset.mutate()} disabled={reset.isPending || !user.isActive}>
                Отправить ссылку для смены пароля
              </button>
            </fieldset>
          )}
          {!user && <p className="faint">Пароль сотрудник задаст сам по ссылке из письма-приглашения.</p>}
        </div>
        <footer>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Отмена
          </button>
          <button className="btn btn-primary" disabled={save.isPending}>
            {save.isPending ? 'Сохраняем…' : 'Сохранить'}
          </button>
        </footer>
      </form>
    </>
  );
}

export function DepartmentsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const departments = useDepartments();
  const [name, setName] = useState('');
  const [headId, setHeadId] = useState('');
  const [editing, setEditing] = useState<{ id: string; name: string; headUserId: string } | null>(null);

  const save = useMutation({
    mutationFn: (d: { id?: string; name: string; headUserId: string }) => {
      const body = { name: d.name, headUserId: d.headUserId || null };
      return d.id ? api.patch(`/departments/${d.id}`, body) : api.post('/departments', body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['departments'] });
      setName('');
      setHeadId('');
      setEditing(null);
      toast('Сохранено');
    },
    onError: (e) => toast(e.message, { error: true }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/departments/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['departments'] });
      toast('Отдел удалён');
    },
    // Сервер называет, что мешает: сотрудники, маршруты или типы заявок.
    onError: (e) => toast(e.message, { error: true }),
  });

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Отделы</h1>
          <p className="muted">
            Руководитель отдела получает согласование там, где в маршруте выбран «Руководитель отдела» или «Руководитель отдела инициатора». Отдел сотрудника выбирается в его карточке.
          </p>
        </div>
      </div>
      <div className="card">
        <form
          className="toolbar"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) save.mutate({ name, headUserId: headId });
          }}
        >
          <input className="input" placeholder="Название нового отдела" value={name} onChange={(e) => setName(e.target.value)} aria-label="Название отдела" />
          <UserSelect value={headId} onChange={setHeadId} placeholder="Руководитель отдела (можно позже)" />
          <button className="btn btn-primary" disabled={!name.trim() || save.isPending}>
            <Icon name="plus" size={18} /> Добавить
          </button>
        </form>
        {departments.data?.length === 0 && <Empty emoji="🏢" title="Отделов пока нет" />}
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Отдел</th>
                <th>Руководитель</th>
                <th>Сотрудников</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {departments.data?.map((d) => (
                <tr key={d.id}>
                  {editing?.id === d.id ? (
                    <td colSpan={4}>
                      <form
                        style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
                        onSubmit={(e) => {
                          e.preventDefault();
                          save.mutate(editing);
                        }}
                      >
                        <input className="input" autoFocus value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} aria-label="Название" style={{ flex: 1, minWidth: 220 }} />
                        <UserSelect value={editing.headUserId} onChange={(id) => setEditing({ ...editing, headUserId: id })} placeholder="Без руководителя" />
                        <button className="btn btn-primary" disabled={!editing.name.trim() || save.isPending}>
                          Сохранить
                        </button>
                        <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                          Отмена
                        </button>
                      </form>
                    </td>
                  ) : (
                    <>
                      <td>
                        <b>{d.name}</b>
                      </td>
                      <td>{d.headName ?? <span className="faint">не назначен</span>}</td>
                      <td className="faint">{d.userCount} сотр.</td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="btn btn-ghost" onClick={() => setEditing({ id: d.id, name: d.name, headUserId: d.headUserId ?? '' })}>
                          Изменить
                        </button>
                        <button
                          className="btn btn-ghost"
                          style={{ color: 'var(--danger)' }}
                          disabled={d.userCount > 0 || remove.isPending}
                          title={d.userCount > 0 ? 'В отделе есть сотрудники — сначала переведите их в другой отдел' : undefined}
                          onClick={() => window.confirm(`Удалить отдел «${d.name}»?`) && remove.mutate(d.id)}
                        >
                          Удалить
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
